// frontend/src/lib/useAuth.js
// ✅ FIXED:
//   1. Backend verification is now AWAITED and BLOCKING — if the server
//      can't verify/upsert the user, sign-in FAILS LOUDLY instead of the
//      UI pretending everything worked (the "Buket problem").
//   2. Listens for "auth:expired" (dispatched by identityGoogle when a
//      stored token dies) and signs out cleanly -> RequireAuth redirects
//      to /landing. No more silent broken sessions.
//   3. One storage shape, written via identityGoogle.persistAuth.

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  persistAuth,
  clearAuth,
  getIdToken as readStoredToken,
  getCurrentUser,
  isTokenExpired,
} from "./identityGoogle.js";

const AuthCtx = createContext(null);

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://artibytes-google-api-191042099955.europe-west4.run.app";

function decodeJwtPayload(credential) {
  const base64Url = credential.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );
  return JSON.parse(jsonPayload);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [idToken, setIdToken] = useState(null);

  // ---------- hydration ----------
  const loadUserFromStorage = useCallback(() => {
    try {
      const storedUser = getCurrentUser();
      const token = readStoredToken();

      // ✅ Don't hydrate a session whose token is already dead —
      // that's how users end up "signed in" but 401ing everywhere.
      if (storedUser && token && !isTokenExpired(token)) {
        setUser(storedUser);
        setIdToken(token);
        console.log("[useAuth] Restored session:", storedUser.email);
      } else if (storedUser || token) {
        console.log("[useAuth] Stored session expired — clearing");
        clearAuth();
        setUser(null);
        setIdToken(null);
      }
    } catch (err) {
      console.warn("[useAuth] Failed to load from storage:", err);
    }
  }, []);

  useEffect(() => {
    loadUserFromStorage();
  }, [loadUserFromStorage]);

  // ---------- sign out ----------
  const signOut = useCallback(async () => {
    setUser(null);
    setIdToken(null);
    clearAuth();
    console.log("[useAuth] Signed out");
  }, []);

  // ---------- expiry + cross-tab sync ----------
  useEffect(() => {
    // identityGoogle dispatches this when a request finds the token expired.
    const onExpired = () => {
      console.log("[useAuth] auth:expired — signing out");
      signOut();
    };
    window.addEventListener("auth:expired", onExpired);

    // Cross-tab: another tab signed in/out.
    const onStorage = (e) => {
      if (
        e.key === null ||
        e.key === "artibytes_auth_v1" ||
        e.key === "artibytes_id_token"
      ) {
        loadUserFromStorage();
      }
    };
    window.addEventListener("storage", onStorage);

    // Safety net: check expiry once a minute while the app is open,
    // so a user idling past the 60-min mark gets a clean redirect
    // instead of a wall of 401s on their next click.
    const interval = setInterval(() => {
      const token = readStoredToken();
      if (token && isTokenExpired(token)) {
        console.log("[useAuth] Session token expired — signing out");
        signOut();
      }
    }, 60 * 1000);

    return () => {
      window.removeEventListener("auth:expired", onExpired);
      window.removeEventListener("storage", onStorage);
      clearInterval(interval);
    };
  }, [signOut, loadUserFromStorage]);

  // ---------- sign in ----------
  const signIn = useCallback(async (credential) => {
    console.log("[useAuth] Google sign-in triggered");

    let normalizedUser;
    try {
      const payload = decodeJwtPayload(credential);
      normalizedUser = {
        googleId: payload.sub,
        email: payload.email,
        name: payload.name || payload.email,
        picture: payload.picture,
      };
    } catch (err) {
      console.error("[useAuth] Could not decode Google credential:", err);
      throw new Error("Sign-in failed: invalid Google credential");
    }

    // ✅ BLOCKING backend verification + user upsert.
    // If this fails, the user is NOT signed in. Period.
    const url = `${API_BASE}/api/auth/google`;
    console.log("[useAuth] Verifying with backend:", url);

    let response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${credential}`,
        },
        body: JSON.stringify({ token: credential }),
        credentials: "include",
      });
    } catch (err) {
      console.error("[useAuth] Backend unreachable:", err);
      throw new Error(
        "Could not reach the server. Check your connection and try again."
      );
    }

    if (!response.ok) {
      let detail = "";
      try {
        const body = await response.json();
        detail = body?.error || "";
      } catch {}
      console.error("[useAuth] Backend rejected sign-in:", response.status, detail);
      throw new Error(
        response.status === 401
          ? "Google sign-in could not be verified. Please try again."
          : `Sign-in failed on the server (${response.status}). Please try again.`
      );
    }

    console.log("[useAuth] ✅ Backend verified + user persisted");

    // Only now do we consider the user signed in.
    persistAuth(normalizedUser, credential);
    setUser(normalizedUser);
    setIdToken(credential);

    // Let interested parties (credits store, etc.) refresh with the new token.
    try {
      window.dispatchEvent(new CustomEvent("auth:signedin"));
    } catch {}

    console.log("[useAuth] ✅ Signed in:", normalizedUser.email);
    return normalizedUser;
  }, []);

  const value = useMemo(
    () => ({ user, idToken, signIn, signOut }),
    [user, idToken, signIn, signOut]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

// Kept for existing imports (lib/api.js debug helper etc.)
export function getIdToken() {
  return readStoredToken();
}